<section
  class="form-settings"
  [class.inspector-mode]="inspectorMode"
  [attr.aria-label]="installed.label + ' form fields'"
>
  <p *ngIf="!inspectorMode" class="hint">
    Changes apply to this action on this step and save with the draft. Preview
    the detail page to see the form.
  </p>
  <p *ngIf="unsupported" role="alert">
    This saved form configuration is malformed or uses an unsupported version.
    Its settings are preserved; restore a valid draft before editing these
    fields.
  </p>
  <p *ngIf="authoringUnavailableReason" role="status">{{ authoringUnavailableReason }}</p>
  <p *ngIf="readerOnly && !unsupported && !authoringUnavailableReason" role="status">
    This saved provided-form configuration is read-only in this editor. Its settings are preserved.
  </p>
  <p *ngIf="!fields.length" role="status">
    This action does not expose editable field settings yet.
  </p>
  <p *ngIf="unavailableFields.length" role="alert">
    Some saved fields are unavailable. Their settings are preserved; restore the
    matching module before publishing.
  </p>
  <p *ngIf="error" role="alert">{{ error }}</p>
  <div
    *ngIf="inspectorMode && selectedFieldId === null"
    class="installed-form-info"
  >
    <strong>{{ installed.label }}</strong>
    <p class="hint">
      {{ fields.length }} fields supplied by this action. Select a field on the
      page to adjust its settings.
    </p>
    <div *ngIf="invalidFields.length" class="issue-list">
      <button
        uiButton
        [wrap]="true"
        [text]="true"
        [outlined]="false"
        type="button"
        *ngFor="let field of invalidFields; trackBy: trackField"
        (click)="selectField(field.name)"
      >
        {{ fieldLabel(field) }}: {{ issue(field) }}
      </button>
    </div>
  </div>
  <p
    *ngIf="inspectorMode && selectedFieldId !== null && !visibleFields.length"
    role="status"
  >
    Select an available field from the page.
  </p>
  <fieldset
    *ngFor="let field of visibleFields; trackBy: trackField"
    [disabled]="unavailable || unsupported || readerOnly"
    [attr.data-form-field]="field.name"
  >
    <legend>{{ fieldLabel(field) }}</legend>
    <div class="field-controls">
      <label
        >Field label<input
          uiControl
          [attr.aria-label]="fieldLabel(field) + ' field label'"
          maxlength="120"
          #labelModel="ngModel"
          [ngModel]="settings(field).label ?? field.label"
          (ngModelChange)="update(field, { label: $event }, labelModel)"
      /></label>
      <label
        >Input type<select
          uiControl
          [attr.aria-label]="fieldLabel(field) + ' input type'"
          #controlModel="ngModel"
          [ngModel]="control(field)"
          (ngModelChange)="setControl(field, $event, controlModel)"
          [disabled]="field.control === 'select' || source(field) === 'lookup'"
        >
          <option value="text">Single line</option>
          <option value="textarea">Paragraph</option>
          <option *ngIf="editableSources(field).length || source(field) === 'lookup'" value="select">
            Dropdown
          </option>
        </select></label
      >
      <label
        >Entry requirement<select
          uiControl
          [attr.aria-label]="fieldLabel(field) + ' entry requirement'"
          #requiredModel="ngModel"
          [ngModel]="settings(field).required ?? field.required"
          (ngModelChange)="update(field, { required: $event }, requiredModel)"
          [disabled]="field.required"
        >
          <option [ngValue]="false">Optional</option>
          <option [ngValue]="true">Required</option></select
        ><small *ngIf="field.required">Required by this action</small
        ><small *ngIf="!field.required"
          >Outcome-specific requirements still apply.</small
        ></label
      >
      <label
        >Maximum characters<input
          uiControl
          type="number"
          min="1"
          step="1"
          [max]="field.maxLength ?? null"
          [attr.aria-label]="fieldLabel(field) + ' maximum characters'"
          [placeholder]="
            field.maxLength ? '' + field.maxLength : 'No additional limit'
          "
          [ngModel]="settings(field).maxLength ?? null"
          #maximumModel="ngModel"
          (ngModelChange)="
            update(field, { maxLength: $event === null ? undefined : $event }, maximumModel)
          "
        /><small *ngIf="field.maxLength"
          >Action limit: {{ field.maxLength }} characters</small
        ></label
      >
      <ng-container *ngIf="layoutEnabled || inspectorMode">
        <h5 class="layout-heading">Layout</h5>
        <label
          >Width<select
            uiControl
            [attr.aria-label]="fieldLabel(field) + ' width'"
            [disabled]="!canEditLayout(field)"
            #widthModel="ngModel"
            [ngModel]="settings(field).layout?.width ?? 'fill'"
            (ngModelChange)="setLayout(field, { width: $event }, widthModel)"
          >
            <option
              *ngFor="let width of widthChoices"
              [value]="width.value"
              [title]="layoutChoiceReason(field, { width: width.value })"
              [disabled]="!!layoutChoiceReason(field, { width: width.value })"
            >
              {{ width.label }}
            </option></select
          ><small
            *ngIf="
              canEditLayout(field) &&
              layoutChoiceReason(field, { width: 'fill' })
            "
            >{{ layoutChoiceReason(field, { width: "fill" }) }}</small
          ></label
        >
        <label
          >Row placement<select
            uiControl
            [attr.aria-label]="fieldLabel(field) + ' row placement'"
            [disabled]="!canEditLayout(field)"
            #rowModel="ngModel"
            [ngModel]="settings(field).layout?.row ?? 'new'"
            (ngModelChange)="setLayout(field, { row: $event }, rowModel)"
          >
            <option value="new">Start new row</option>
            <option
              value="previous"
              [disabled]="!!layoutChoiceReason(field, { row: 'previous' })"
            >
              Same row as previous field
            </option></select
          ><small *ngIf="layoutChoiceReason(field, { row: 'previous' })">{{
            layoutChoiceReason(field, { row: "previous" })
          }}</small></label
        >
      </ng-container>
    </div>
    <div *ngIf="control(field) === 'select'" class="choice-settings">
      <label
        >Dropdown list source<select
          uiControl
          [attr.aria-label]="fieldLabel(field) + ' dropdown list source'"
          #sourceModel="ngModel"
          [ngModel]="source(field)"
          (ngModelChange)="setSource(field, $event, sourceModel)"
        >
          <option *ngFor="let source of editableSources(field)" [value]="source">
            {{ sourceLabel(source) }}
          </option>
          <option *ngIf="source(field) === 'lookup' || lookupSourceAvailable && lookupPermitted(field)" value="lookup" [disabled]="!canRequestLookup(field)">Lookup choices{{ readerOnly ? ' (read-only)' : '' }}</option>
        </select></label
      >
      <button *ngIf="source(field) === 'lookup'" uiButton type="button"
        [disabled]="!canRequestLookup(field)" [title]="lookupSourceReason"
        [attr.aria-label]="'Change lookup source for ' + fieldLabel(field)"
        (click)="requestLookupSource(field)">Change lookup source</button>
      <small *ngIf="lookupSourceReason && lookupPermitted(field)">{{ lookupSourceReason }}</small>
      <p class="hint" *ngIf="source(field) === 'step-outcomes'">
        Uses the outcome labels configured for this step. Stored values continue
        to drive its paths.
      </p>
      <p *ngIf="settings(field).lookup as binding" class="hint" data-provided-lookup>
        Saved lookup: {{ binding.configurationCode }} / {{ binding.domainCode }}.
        {{ lookupRuntimeAvailable ? 'Choices load in the task. Preview does not read lookup sources.' : 'Choices are unavailable until this action provides versioned runtime support.' }}
      </p>
      <div
        *ngIf="source(field) === 'custom-options'; else sourceChoices"
        class="custom-options"
        cdkDropList
        [cdkDropListData]="options(field)"
        [cdkDropListDisabled]="unavailable || unsupported || readerOnly"
        (cdkDropListDropped)="dropOption(field, $event)"
      >
        <div
          *ngFor="
            let option of options(field);
            let i = index;
            trackBy: trackOption
          "
          class="choice-row"
          cdkDrag
          [cdkDragDisabled]="unavailable || unsupported || readerOnly"
        >
          <span
            cdkDragHandle
            class="choice-drag-handle"
            aria-hidden="true"
            title="Drag to reorder choice"
            >⋮⋮</span
          >
          <label
            >Stored value<input
              uiControl
              [attr.aria-label]="
                fieldLabel(field) + ' choice ' + (i + 1) + ' value'
              "
              [ngModel]="option.value"
              (ngModelChange)="updateOption(field, i, { value: $event })"
          /></label>
          <label
            >Display label<input
              uiControl
              maxlength="120"
              [attr.aria-label]="
                fieldLabel(field) + ' choice ' + (i + 1) + ' label'
              "
              [ngModel]="option.label"
              (ngModelChange)="updateOption(field, i, { label: $event })"
          /></label>
          <div class="choice-actions">
            <button
              uiButton
              type="button"
              [disabled]="i === 0"
              [attr.aria-label]="
                'Move ' + fieldLabel(field) + ' choice ' + (i + 1) + ' earlier'
              "
              title="Move choice earlier"
              (click)="moveOption(field, i, -1)"
            >
              ↑
            </button>
            <button
              uiButton
              type="button"
              [disabled]="i === options(field).length - 1"
              [attr.aria-label]="
                'Move ' + fieldLabel(field) + ' choice ' + (i + 1) + ' later'
              "
              title="Move choice later"
              (click)="moveOption(field, i, 1)"
            >
              ↓
            </button>
            <button
              uiButton
              severity="danger"
              [text]="true"
              [outlined]="false"
              type="button"
              [attr.aria-label]="
                fieldLabel(field) + ' remove choice ' + (i + 1)
              "
              (click)="removeOption(field, i)"
            >
              Remove
            </button>
          </div>
        </div>
        <button
          uiButton
          type="button"
          [disabled]="options(field).length >= 100"
          (click)="addOption(field)"
        >
          Add choice
        </button>
      </div>
      <ng-template #sourceChoices
        ><ul class="source-choices">
          <li *ngFor="let option of options(field)">
            {{ option.label }} <span>{{ option.value }}</span>
          </li>
        </ul></ng-template
      >
      <small>Only sources supported by this action are available.</small>
    </div>
    <p *ngIf="issue(field)" role="alert">{{ issue(field) }}</p>
    <button
      uiButton
      class="reset"
      type="button"
      [disabled]="!changed(field)"
      (click)="reset(field)"
    >
      Restore field defaults
    </button>
  </fieldset>
</section>
